/**
 * Rigid source-to-piece pose math (design §5). One applier for the whole
 * editor: worldPoint = R(rotDeg) · (p − centroid) + centroid + t. Rotation is
 * about the geometry's own centroid so translation and rotation stay
 * independent — rotating a piece in place never changes txMm/tyMm.
 *
 * Frames: canonical millimetres, y-up, degrees counter-clockwise.
 */
import type { PiecePose, Ring } from '../../../../worker/api/saya.contract'

export const IDENTITY_POSE: PiecePose = Object.freeze({ rotDeg: 0, txMm: 0, tyMm: 0 })

export function normalizeDeg(deg: number): number {
  const wrapped = ((deg % 360) + 360) % 360
  return wrapped > 180 ? wrapped - 360 : wrapped === 0 ? 0 : wrapped
}

export function applyPoseToPoint(
  p: readonly [number, number],
  pose: PiecePose,
  centroid: readonly [number, number],
): [number, number] {
  const rad = (pose.rotDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = p[0] - centroid[0]
  const dy = p[1] - centroid[1]
  return [
    centroid[0] + dx * cos - dy * sin + pose.txMm,
    centroid[1] + dx * sin + dy * cos + pose.tyMm,
  ]
}

export function applyPose(
  ring: Ring,
  pose: PiecePose | null,
  centroid: readonly [number, number],
): Ring {
  if (!pose || (pose.rotDeg === 0 && pose.txMm === 0 && pose.tyMm === 0)) {
    return ring.map(([x, y]) => [x, y])
  }
  return ring.map((p) => applyPoseToPoint(p, pose, centroid))
}

export function rotatePose(pose: PiecePose | null, deltaDeg: number): PiecePose {
  const base = pose ?? IDENTITY_POSE
  return { ...base, rotDeg: normalizeDeg(base.rotDeg + deltaDeg) }
}

export function movePose(pose: PiecePose | null, dxMm: number, dyMm: number): PiecePose {
  const base = pose ?? IDENTITY_POSE
  return { ...base, txMm: base.txMm + dxMm, tyMm: base.tyMm + dyMm }
}

export function rotateUnitVec(v: readonly [number, number], deg: number): [number, number] {
  const rad = (deg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return [v[0] * cos - v[1] * sin, v[0] * sin + v[1] * cos]
}

/**
 * Major axis of the vertex cloud via the 2×2 covariance eigenvector — a
 * SUGGESTION for grain alignment (vertex-density-biased, which is fine for a
 * suggestion; the operator confirms). Degrees in (-90, 90], 0 = x-axis.
 */
export function principalAxisDeg(ring: Ring): number {
  const n = ring.length
  if (n === 0) return 0
  let mx = 0
  let my = 0
  for (const [x, y] of ring) {
    mx += x
    my += y
  }
  mx /= n
  my /= n
  let sxx = 0
  let sxy = 0
  let syy = 0
  for (const [x, y] of ring) {
    const dx = x - mx
    const dy = y - my
    sxx += dx * dx
    sxy += dx * dy
    syy += dy * dy
  }
  const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy)
  const deg = (theta * 180) / Math.PI
  return deg <= -90 ? deg + 180 : deg > 90 ? deg - 180 : deg
}
